import { ApiResult, HttpClient } from "../http.js";
import { PagedWalkthroughs, WalkthroughDto, WalkthroughItemPhotoLinkDto, WalkthroughItemResultDto, WalkthroughMode, WalkthroughPdfDownloadDto, WalkthroughRefreshFromTemplateResultDto, WalkthroughStatus } from "../types/walkthroughs.js";

export interface CreateWalkthroughPayload {
  title: string;
  mode: WalkthroughMode;
  customerId?: string | null;
  templateId?: string | null;
  customerName?: string | null;
  customerAddress?: string | null;
  id?: string;
}

export interface UpdateWalkthroughPayload {
  title: string;
  status: WalkthroughStatus;
  customerId?: string | null;
  quoteId?: string | null;
  templateId?: string | null;
  customerName?: string | null;
  customerAddress?: string | null;
  categoriesJson?: string | null;
  completedAt?: string | null;
}

export interface AppendWalkthroughItemPayload {
  /** Client-generated id; becomes the item id and makes the call idempotent. */
  clientItemId: string;
  categoryName?: string | null;
  name: string;
  note?: string | null;
  quantity?: number | null;
  unit?: string | null;
}

export interface ListWalkthroughsQuery {
  page?: number;
  pageSize?: number;
  /** Server-side match against title, customer name, customer address and creator name. */
  search?: string;
  createdById?: string;
  quoteId?: string;
  /** Filters to walkthroughs with no quoteId — the "not yet tied to a tilbud" set. */
  unlinkedOnly?: boolean;
  statuses?: WalkthroughStatus[];
  /** One of "customerName" | "customerAddress" | "createdByName" | "startedAt". Any other
   *  value (or omitted) keeps the server's default order (updatedAt desc). */
  sortBy?: string;
  sortDescending?: boolean;
}

export class WalkthroughsApi {
  constructor(private readonly http: HttpClient) {}

  list(query: ListWalkthroughsQuery = {}): Promise<ApiResult<PagedWalkthroughs>> {
    return this.http.get<PagedWalkthroughs>("/api/walkthroughs", { query: { ...query } });
  }

  get(id: string): Promise<ApiResult<WalkthroughDto>> {
    return this.http.get<WalkthroughDto>(`/api/walkthroughs/${encodeURIComponent(id)}`);
  }

  exportPdf(id: string): Promise<ApiResult<WalkthroughPdfDownloadDto>> {
    return this.http.get<WalkthroughPdfDownloadDto>(`/api/walkthroughs/${encodeURIComponent(id)}/pdf`);
  }

  create(payload: CreateWalkthroughPayload): Promise<ApiResult<string>> {
    return this.http.post<string>("/api/walkthroughs", { body: payload });
  }

  update(id: string, payload: UpdateWalkthroughPayload): Promise<ApiResult<string>> {
    return this.http.put<string>(`/api/walkthroughs/${encodeURIComponent(id)}`, { body: payload });
  }

  /** Reopen a completed walkthrough for editing (Completed → InProgress). */
  reopen(id: string): Promise<ApiResult<string>> {
    return this.http.post<string>(`/api/walkthroughs/${encodeURIComponent(id)}/reopen`);
  }

  /** Re-apply the walkthrough's template ("Opdater fra skabelon") — server-side
   * merge where user-entered quantities, notes and photos always win. */
  refreshFromTemplate(id: string): Promise<ApiResult<WalkthroughRefreshFromTemplateResultDto>> {
    return this.http.post<WalkthroughRefreshFromTemplateResultDto>(
      `/api/walkthroughs/${encodeURIComponent(id)}/refresh-from-template`,
    );
  }

  /** Append an item to a simple-mode walkthrough (409 for a structured one). */
  appendItem(id: string, payload: AppendWalkthroughItemPayload): Promise<ApiResult<WalkthroughItemResultDto>> {
    return this.http.post<WalkthroughItemResultDto>(
      `/api/walkthroughs/${encodeURIComponent(id)}/items`,
      { body: payload },
    );
  }

  /** Link a photo already uploaded with owner Walkthrough to an item. */
  linkItemPhoto(id: string, itemId: string, photoId: string): Promise<ApiResult<WalkthroughItemPhotoLinkDto>> {
    return this.http.post<WalkthroughItemPhotoLinkDto>(
      `/api/walkthroughs/${encodeURIComponent(id)}/items/${encodeURIComponent(itemId)}/photos`,
      { body: { photoId } },
    );
  }

  remove(id: string): Promise<ApiResult<string>> {
    return this.http.delete<string>(`/api/walkthroughs/${encodeURIComponent(id)}`);
  }
}
